import React, { useMemo, useCallback, useState } from 'react';
import { map, findIndex, find } from 'lodash';
import { useFormikContext, FieldArray, Form } from 'formik';
import { Typography, Grid } from '@mui/material';
import { LoaderBackdrop } from '@shared/components/LoaderBackdrop';
import { IMenuValues, IMenuItem, IChildMenuItem, TNullableAnyItem } from '../types';
import { IRole, IQueue } from '../../QueueConfiguration/types';
import { useMenuItemFormConfig } from '../hooks/useMenuItemFormConfig';
import { useButtonsConfig } from '../hooks/useButtonsConfig';
import { FormPart } from '../../../../shared/components/FormPart';
import { SubHeaderButtons } from './SubHeaderButtons';
import { MenuEditorTree } from './MenuEditorTree';

interface IProps {
  expanded: string[];
  selectedMenuItem: TNullableAnyItem;
  roleOptions: IRole[];
  queueOptions: IQueue[];
  loading: boolean
  setExpanded: (expanded: string[]) => void;
  setSelectedMenuItem: (menuItem: TNullableAnyItem) => void;
}

export const MenuEditorForm = ({
  expanded,
  selectedMenuItem,
  roleOptions,
  queueOptions,
  loading,
  setExpanded,
  setSelectedMenuItem,
}: IProps) => {
  const [disabledIds, setDisabledIds] = useState<string[]>();
  const {
    values: { menuItems: menuItemsValues },
    isValid,
    dirty,
    isSubmitting,
    setFieldValue,
  } = useFormikContext<IMenuValues>();

  const setMenuItemsValues = useCallback(
    (updatedValues: IMenuItem[]) => setFieldValue('menuItems', updatedValues),
    [setFieldValue],
  );

  const menuItemPrefix = useMemo<string | null>(() => {
    if (!selectedMenuItem) {
      return null;
    }

    const { id, parentId } = selectedMenuItem;

    if (!parentId) {
      const itemIndex: number = findIndex(menuItemsValues, ['id', id]);

      return `menuItems[${itemIndex}]`;
    }

    const parentIndex: number = findIndex(menuItemsValues, ['id', parentId]);
    const itemIndex: number = findIndex(menuItemsValues[parentIndex].childMenuItems, ['id', id]);

    return `menuItems[${parentIndex}].childMenuItems[${itemIndex}]`;
  }, [selectedMenuItem, menuItemsValues]);

  const menuItems = useMemo<IMenuItem[]>(
    () =>
      map<IMenuItem, IMenuItem>(menuItemsValues, ({ childMenuItems, queueId, ...menuItem }) => ({
        ...menuItem,
        queueId: !queueId ? null : find(queueOptions, ['id', queueId])?.subgroup ?? null,
        childMenuItems: map<IChildMenuItem, IChildMenuItem>(
          childMenuItems,
          ({ queueId: childQueueId, ...childItem }) => ({
            ...childItem,
            queueId: !childQueueId ? null : find(queueOptions, ['id', childQueueId])?.subgroup ?? null,
          }),
        ),
      })),
    [menuItemsValues],
  );

  const { menuItemFormConfig } = useMenuItemFormConfig({
    menuItemPrefix,
    roleOptions,
    queueOptions,
    menuItemsValues,
    setFieldValue,
  });

  const { buttonsConfig } = useButtonsConfig({
    isValid,
    dirty,
    isSubmitting,
    expanded,
    selectedMenuItem,
    menuItemsValues,
    disabledIds,
    setMenuItemsValues,
    setSelectedMenuItem,
    setExpanded,
  });

  return (
    <Form>
     <LoaderBackdrop open={loading} />
      <SubHeaderButtons buttonsConfig={buttonsConfig} />

      {dirty && (
        <Typography paragraph color="error">
          Unsaved Changes
        </Typography>
      )}

      <Grid container spacing={2}>
        <Grid item xs={8}>
          <FieldArray name="menuItems">
            {() => (
              <MenuEditorTree
                menuItems={menuItems}
                menuItemsValues={menuItemsValues}
                expandedKeys={expanded}
                selectedKeys={selectedMenuItem ? [selectedMenuItem.id] : []}
                setDisabledIds={setDisabledIds}
                setExpanded={setExpanded}
                setSelectedMenuItem={setSelectedMenuItem}
                setMenuItemsValues={setMenuItemsValues}
              />
            )}
          </FieldArray>
        </Grid>

        <Grid item xs={4}>
          <FormPart config={menuItemFormConfig} />
        </Grid>
      </Grid>
    </Form>
  );
};
